import { useCallback, useEffect, useState } from 'react';
import { Search, Eye, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { customerService, type CustomerCart } from '../services/customerService';
import { fieldClass, labelClass, formatMoney } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';
import type { Customer } from '../types';

type CustomerEditForm = {
  name: string;
  email: string;
  phone: string;
  isActive: boolean;
  isBasalam: boolean;
};

const emptyEditForm = (): CustomerEditForm => ({
  name: '',
  email: '',
  phone: '',
  isActive: true,
  isBasalam: false,
});

export function CustomersPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const itemsPerPage = 10;

  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [viewCustomer, setViewCustomer] = useState<Customer | null>(null);
  const [viewCart, setViewCart] = useState<CustomerCart | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<CustomerEditForm>(emptyEditForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customerService.getCustomers({
        page: currentPage,
        limit: itemsPerPage,
        q: searchTerm.trim() || undefined,
      });
      setCustomers(res.data ?? []);
      setTotalPages(Math.max(1, res.totalPages));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت مشتریان');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredCustomers = customers;

  const openView = async (id: string) => {
    setViewOpen(true);
    setViewCustomer(null);
    setViewCart(null);
    setDetailLoading(true);
    try {
      const [data, cart] = await Promise.all([
        customerService.getCustomer(id),
        customerService.getCustomerCart(id).catch(() => null),
      ]);
      setViewCustomer(data);
      setViewCart(cart);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت جزئیات مشتری');
      setViewOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = async (id: string) => {
    setEditOpen(true);
    setEditId(id);
    setForm(emptyEditForm());
    setDetailLoading(true);
    try {
      const data = await customerService.getCustomer(id);
      setForm({
        name: data.name ?? '',
        email: data.email ?? '',
        phone: data.isBasalam ? '' : data.phone ?? '',
        isActive: Boolean(data.isActive),
        isBasalam: Boolean(data.isBasalam),
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت مشتری');
      setEditOpen(false);
      setEditId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeView = () => {
    setViewOpen(false);
    setViewCustomer(null);
    setViewCart(null);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditId(null);
    setForm(emptyEditForm());
  };

  const handleSave = async () => {
    if (!editId) return;
    if (!form.name.trim()) {
      toast.error('نام الزامی است');
      return;
    }
    setSaving(true);
    try {
      await customerService.updateCustomer(editId, {
        name: form.name.trim(),
        email: form.email.trim(),
        ...(form.isBasalam ? {} : { phone: form.phone.trim() }),
        isActive: form.isActive,
      });
      toast.success('مشتری به‌روزرسانی شد');
      closeEdit();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'به‌روزرسانی ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">مشتریان</h1>
          <p className="text-slate-600 mt-1">مدیریت لیست مشتریان</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="جستجو به نام، ایمیل یا شماره..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pr-10 pl-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-right"
            dir="rtl"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="admin-table-scroll">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">ردیف</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">نام</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">ایمیل</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">شماره موبایل</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">تعداد سفارش</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">مجموع خرید</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">وضعیت</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                    مشتری‌ای یافت نشد
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer, index) => (
                  <tr
                    key={customer.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm tabular-nums text-slate-500">
                      {rowNumber(index, currentPage, itemsPerPage)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                          {customer.name?.charAt(0) || '—'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{customer.name || '—'}</p>
                          {customer.isBasalam ? (
                            <span className="mt-1 inline-block rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                              کاربر باسلام
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{customer.email || '—'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {customer.isBasalam ? '—' : customer.phone || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                      {customer.orderCount ?? 0}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                      {((customer.totalSpent ?? 0) / 1000000).toFixed(1)}M
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${
                          customer.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {customer.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-indigo-600 hover:text-indigo-700 p-1"
                          onClick={() => void openView(customer.id)}
                          title="مشاهده"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="text-blue-600 hover:text-blue-700 p-1"
                          onClick={() => void openEdit(customer.id)}
                          title="ویرایش"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-center">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      <Modal isOpen={viewOpen} onClose={closeView} title="جزئیات مشتری">
        {detailLoading || !viewCustomer ? (
          <p className="text-center text-slate-500 py-6">در حال بارگذاری...</p>
        ) : (
          <div className="space-y-6" dir="rtl">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4 border-b border-slate-100 pb-2">
                <dt className="text-slate-500">نام</dt>
                <dd className="font-medium text-slate-900">
                  {viewCustomer.name || '—'}
                  {viewCustomer.isBasalam ? (
                    <span className="mr-2 inline-block rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                      کاربر باسلام
                    </span>
                  ) : null}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-slate-100 pb-2">
                <dt className="text-slate-500">ایمیل</dt>
                <dd className="font-medium text-slate-900" dir="ltr">
                  {viewCustomer.email || '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-slate-100 pb-2">
                <dt className="text-slate-500">شماره موبایل</dt>
                <dd className="font-medium text-slate-900" dir="ltr">
                  {viewCustomer.isBasalam ? '—' : viewCustomer.phone || '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-slate-100 pb-2">
                <dt className="text-slate-500">تعداد سفارش</dt>
                <dd className="font-medium text-slate-900">{viewCustomer.orderCount ?? 0}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-slate-100 pb-2">
                <dt className="text-slate-500">مجموع خرید</dt>
                <dd className="font-medium text-slate-900">
                  {formatMoney(viewCustomer.totalSpent ?? 0)} تومان
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-slate-100 pb-2">
                <dt className="text-slate-500">وضعیت</dt>
                <dd>
                  <span
                    className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${
                      viewCustomer.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {viewCustomer.isActive ? 'فعال' : 'غیرفعال'}
                  </span>
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">تاریخ عضویت</dt>
                <dd className="font-medium text-slate-900">
                  {viewCustomer.createdAt
                    ? new Date(viewCustomer.createdAt).toLocaleDateString('fa-IR')
                    : '—'}
                </dd>
              </div>
            </dl>

            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">سبد خرید فعلی</h3>
              {!viewCart || viewCart.items.length === 0 ? (
                <p className="text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-4 text-center">
                  سبد خرید این مشتری خالی است
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {viewCart.items.map((it) => (
                    <div
                      key={`${it.productId}-${it.color ?? ''}-${it.variant ?? ''}`}
                      className="flex items-center gap-3 rounded-lg border border-slate-100 p-2"
                    >
                      {it.image ? (
                        <img src={it.image} alt="" className="h-10 w-10 rounded object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-slate-100" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{it.name}</p>
                        {(it.color || it.variant) && (
                          <p className="text-xs text-indigo-600 mt-0.5">
                            {it.color ? `رنگ: ${it.color}` : ''}
                            {it.color && it.variant ? ' · ' : ''}
                            {it.variant ? `نوع: ${it.variant}` : ''}
                          </p>
                        )}
                        <p className="text-xs text-slate-500">
                          {formatMoney(Number(it.discountPrice ?? it.price))} تومان
                        </p>
                      </div>
                      <span className="text-sm font-bold text-indigo-700">×{it.qty}</span>
                    </div>
                  ))}
                  <p className="text-xs text-slate-500 pt-1">
                    جمع: {viewCart.totalQty} عدد در {viewCart.itemCount} ردیف
                    {viewCart.updatedAt
                      ? ` · آخرین تغییر ${new Date(viewCart.updatedAt).toLocaleString('fa-IR')}`
                      : ''}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={editOpen}
        onClose={closeEdit}
        title="ویرایش مشتری"
        footer={
          <>
            <button
              type="button"
              onClick={closeEdit}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || detailLoading}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        {detailLoading ? (
          <p className="text-center text-slate-500 py-6">در حال بارگذاری...</p>
        ) : (
          <div className="space-y-4" dir="rtl">
            <div>
              <label className={labelClass}>نام</label>
              <input
                className={fieldClass}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className={labelClass}>ایمیل</label>
              <input
                type="email"
                className={fieldClass}
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                dir="ltr"
              />
            </div>
            <div>
              <label className={labelClass}>شماره موبایل</label>
              <input
                className={fieldClass}
                value={form.isBasalam ? '' : form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                dir="ltr"
                disabled={form.isBasalam}
                placeholder={form.isBasalam ? 'کاربر باسلام — بدون شماره' : undefined}
              />
              {form.isBasalam ? (
                <p className="mt-1 text-xs text-amber-700">این مشتری از نظرات باسلام است و شماره تماس ندارد.</p>
              ) : null}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              فعال
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
}
